import apiClient from '@/repositories/apiClient';
import { uploadNewsMediaFile } from '@/redux/new_services/newsMedia';

// Probe a file's natural dimensions before sending it to the conversion
// pipeline so the worker can size thumbnails correctly.
const probeDimensions = (file) =>
  new Promise((resolve) => {
    if (file.type?.startsWith('video/')) {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => resolve({ width: v.videoWidth, height: v.videoHeight });
      v.onerror = () => resolve({ width: 0, height: 0 });
      v.src = URL.createObjectURL(file);
    } else {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = URL.createObjectURL(file);
    }
  });

// Run the two-step upload + register cycle and return the freshly created
// NewsMedia row. Caller is responsible for polling /api/news-media/{id}
// until status flips to "ready" if it needs the final S3 URLs.
export const uploadAndRegisterNewsMedia = async (file, onProgress) => {
  const { width, height } = await probeDimensions(file);
  const uploaded = await uploadNewsMediaFile(file, onProgress);
  const response = await apiClient({
    url: '/news-media',
    method: 'POST',
    data: {
      name: file.name,
      filename: uploaded.filename,
      real_filename: uploaded.real_filename,
      content_type: uploaded.content_type,
      width,
      height,
    },
  });
  return response.data?.data;
};

// Wait until a freshly uploaded NewsMedia row finishes conversion. Caps
// the wait so the editor never hangs forever on a stuck pipeline. On
// failure we forward the worker's error_reason so the toast in the
// editor toolbar tells the operator what actually went wrong instead of
// just "Konvertierung fehlgeschlagen".
const waitUntilReady = async (id, { timeoutMs = 120_000 } = {}) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const resp = await apiClient({ url: `/news-media/${id}`, method: 'GET' });
    const fresh = resp.data?.data;
    if (fresh?.status === 'ready') return fresh;
    if (fresh?.status === 'failed') {
      const reason = fresh.error_reason || 'Konvertierung fehlgeschlagen';
      throw new Error(reason);
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
  throw new Error('Konvertierung dauert zu lange');
};

// uploadNewsImageInline is used by the RTE: upload a file, then poll until
// the conversion finishes and resolve with a usable image URL. Falls back
// to the s3_med_url which is good enough for inline display.
export const uploadNewsImageInline = async (file) => {
  const created = await uploadAndRegisterNewsMedia(file);
  if (!created) throw new Error('Upload fehlgeschlagen');
  const fresh = await waitUntilReady(created.model.ID);
  return fresh.s3_med_url || fresh.s3_url;
};

// uploadNewsVideoInline mirrors the image variant but resolves to the full
// converted video file (s3_url). News article videos render without a
// poster — the player just shows the first frame on hover and the user
// clicks the video to switch to full screen.
export const uploadNewsVideoInline = async (file) => {
  const created = await uploadAndRegisterNewsMedia(file);
  if (!created) throw new Error('Upload fehlgeschlagen');
  // Videos take longer to transcode than image thumbnailing — extend the
  // wait window a bit so a normal-sized clip survives the poll loop.
  const fresh = await waitUntilReady(created.model.ID, { timeoutMs: 5 * 60_000 });
  return { src: fresh.s3_url };
};
